import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../utils/config";
import AvatarViewer from "./AvatarViewer";
import TeamBadgeList from "./TeamBadgeList";
import { ROLE_LABEL } from "../utils/roleLabels";

const NAV_ITEMS = [
  { to: "/dashboard",    label: "Dashboard"       },
  { to: "/items",        label: "Barang"          },
  { to: "/categories",   label: "Kategori"        },
  { to: "/locations",    label: "Lokasi"          },
  { to: "/transactions", label: "Transaksi"       },
  { to: "/barang-di-luar", label: "Barang di Luar" },
  { to: "/damaged",      label: "Barang Rusak"    },
  { to: "/panel-assemblies", label: "Perakitan Panel" },
];

// "Order Panel" is hidden from the menu for now (not deleted) — still
// unused. Route/page/backend are untouched; add the row back here to unhide.
const PANEL_ITEMS = [
  { to: "/admin/panel-assemblies", label: "Approval Rakitan" },
];

const SUPERADMIN_PANEL_ITEMS = [
  { to: "/panel-templates", label: "Template Panel" },
];

/* ── Administrasi module (Sekretaris / Bendahara) ── */
const SEKRETARIS_ITEMS = [
  { to: "/administrasi/sekretaris/form-pengajuan", label: "Form Pengajuan" },
  { to: "/administrasi/sekretaris/surat-masuk",    label: "Surat Masuk"    },
  { to: "/administrasi/sekretaris/surat-keluar",   label: "Surat Keluar"   },
  { to: "/administrasi/sekretaris/arsip",          label: "Arsip Dokumen"  },
];

const BENDAHARA_ITEMS = [
  { to: "/administrasi/bendahara/anggaran",       label: "Anggaran"       },
  { to: "/administrasi/bendahara/pengajuan-dana", label: "Pengajuan Dana" },
  { to: "/administrasi/bendahara/kas-masuk",      label: "Kas Masuk"      },
  { to: "/administrasi/bendahara/realisasi-nota", label: "Realisasi Nota" },
  { to: "/administrasi/bendahara/kas-keluar",     label: "Kas Keluar"     },
  { to: "/administrasi/bendahara/lpj",            label: "LPJ"            },
];

// Each division manages its own letter templates (the page shows only the
// divisions you belong to). Superadmin reaches the same page from Master Data.
const TEMPLATE_ITEM = { to: "/administrasi/template-manager", label: "Template Surat" };
// The one active event is run by the divisions too, not only by Super Admin.
const EVENT_ITEM = { to: "/administrasi/folder-config", label: "Event & Folder" };

const SEKRETARIS_NAV = [
  { to: "/dashboard", label: "Dashboard" },
  ...SEKRETARIS_ITEMS,
  EVENT_ITEM,
  TEMPLATE_ITEM,
];

const BENDAHARA_NAV = [
  { to: "/dashboard", label: "Dashboard" },
  ...BENDAHARA_ITEMS,
  EVENT_ITEM,
  TEMPLATE_ITEM,
];

// Super Admin sees everything, grouped into a dropdown tree so the sidebar
// stays readable as more roles/modules are added. Adding a future role's
// menu here never touches the flat-list rendering the other roles use.
// `isLabel` rows are the mono section captions ("MODUL", "SISTEM").
const SUPERADMIN_TREE = [
  { to: "/dashboard", label: "Dashboard" },
  { isLabel: true, label: "MODUL" },
  {
    key: "inventory", label: "Inventory",
    children: [
      ...NAV_ITEMS.filter((item) => item.to !== "/dashboard"),
      ...PANEL_ITEMS,
      ...SUPERADMIN_PANEL_ITEMS,
      { to: "/loan-transactions", label: "Peminjaman" },
    ],
  },
  {
    key: "administrasi", label: "Administrasi",
    children: [
      { key: "administrasi-sekretaris", label: "Sekretaris", children: SEKRETARIS_ITEMS },
      { key: "administrasi-bendahara",  label: "Bendahara",  children: BENDAHARA_ITEMS  },
    ],
  },
  { isLabel: true, label: "SISTEM" },
  {
    key: "masterdata", label: "Master Data",
    children: [
      { to: "/users",    label: "Users"     },
      { to: "/roles",    label: "Role"      },
      { to: "/audit",    label: "Audit Log" },
      { to: "/debug-log", label: "Debug Log" },
      { to: "/settings", label: "Settings"  },
      { to: "/administrasi/folder-config",    label: "Event & Folder" },
      { to: "/administrasi/template-manager", label: "Template Surat"      },
    ],
  },
];

// Collects the group keys that are ancestors of the given path, so that
// navigating (or loading) into a nested item auto-opens its parent group(s).
function ancestorGroupKeys(nodes, pathname, trail = []) {
  for (const node of nodes) {
    if (node.to) {
      if (pathname === node.to || pathname.startsWith(`${node.to}/`)) return trail;
    } else if (node.children) {
      const found = ancestorGroupKeys(node.children, pathname, [...trail, node.key]);
      if (found) return found;
    }
  }
  return null;
}

function Sidebar({ mobileOpen = false, onMobileClose }) {
  const [user, setUser] = useState(null);
  const location        = useLocation();
  const navigate        = useNavigate();

  // Only Super Admin's menu has dropdown groups; every other role keeps the
  // flat list it always had, so this state is simply unused for them.
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    fetch(`${API_BASE_URL}/api/auth/me/`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then(setUser)
      .catch(() => setUser(null));
  }, []);

  const USER_NAV = [
    ...NAV_ITEMS.filter(
      (item) => item.to === "/transactions" || item.to === "/panel-assemblies"
    ),
    { to: "/my-loan-requests", label: "Pengajuan Pinjam" },
  ];

  // An admin's tree is no longer static: it grows a Sekretaris/Bendahara
  // group for each module their kelompok additionally grants, on top of the
  // inventory nav every admin already had (hasil meet.txt case 1 — accessible_modules
  // comes from MeSerializer/inventory.teams.user_modules, so this can never
  // drift from what the backend permission classes actually allow).
  const modules = user?.accessible_modules || [];
  const navTree = useMemo(() => {
    if (user?.role === "superadmin") return SUPERADMIN_TREE;
    if (user?.role === "admin") {
      const tree = [...NAV_ITEMS, ...PANEL_ITEMS, { to: "/loan-transactions", label: "Peminjaman" }];
      if (modules.includes("sekretaris")) {
        tree.push({ key: "administrasi-sekretaris", label: "Sekretaris", children: SEKRETARIS_ITEMS });
      }
      if (modules.includes("bendahara")) {
        tree.push({ key: "administrasi-bendahara", label: "Bendahara", children: BENDAHARA_ITEMS });
      }
      // An admin in an Administrasi kelompok runs that division's templates too.
      if (modules.includes("sekretaris") || modules.includes("bendahara")) {
        tree.push(EVENT_ITEM, TEMPLATE_ITEM);
      }
      return tree;
    }
    if (user?.role === "sekretaris") {
      const tree = [...SEKRETARIS_NAV];
      if (modules.includes("bendahara")) {
        tree.push({ key: "administrasi-bendahara", label: "Bendahara", children: BENDAHARA_ITEMS });
      }
      return tree;
    }
    if (user?.role === "bendahara") {
      const tree = [...BENDAHARA_NAV];
      if (modules.includes("sekretaris")) {
        tree.push({ key: "administrasi-sekretaris", label: "Sekretaris", children: SEKRETARIS_ITEMS });
      }
      return tree;
    }
    return USER_NAV;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, modules.join(",")]);

  // Auto-open the group(s) containing the current route once, the first time
  // a grouped tree (Super Admin, or anyone whose kelompok grants a second
  // module) is available — after that, expand/collapse is fully up to the
  // user. Whether a role's tree has groups at all is data-dependent now
  // (e.g. a Bendahara-role user only gets a Sekretaris group if they also
  // hold a Sekretariat kelompok), so check the tree itself instead of role.
  useEffect(() => {
    const hasGroups = navTree.some((node) => node.children);
    if (!hasGroups) return;
    const trail = ancestorGroupKeys(navTree, location.pathname);
    if (!trail || !trail.length) return;
    setExpandedGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      trail.forEach((key) => {
        if (!next[key]) { next[key] = true; changed = true; }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, navTree]);

  const toggleGroup = (key) =>
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const isActivePath = (to) =>
    location.pathname === to ||
    location.pathname.startsWith(`${to}/`) ||
    (to === "/dashboard" && location.pathname === "/");

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    navigate("/login", { replace: true });
  };

  const initials = user?.username ? user.username.substring(0, 2).toUpperCase() : "??";

  const renderNodes = (nodes, depth) =>
    nodes.map((node) => {
      if (node.isLabel) {
        return (
          <div key={`label-${node.label}`} className="sb-label">
            {node.label}
          </div>
        );
      }

      const pad = { paddingLeft: `${10 + depth * 16}px` };
      const nested = depth > 0 ? " sb-nested" : "";

      if (node.to) {
        const active = isActivePath(node.to);
        return (
          <Link
            key={node.to}
            to={node.to}
            onClick={onMobileClose}
            aria-current={active ? "page" : undefined}
            style={pad}
            className={`sb-item${nested}${active ? " active" : ""}`}
          >
            <span className="sb-dot" />
            <span className="sb-text">{node.label}</span>
          </Link>
        );
      }

      const isOpen = !!expandedGroups[node.key];
      return (
        <Fragment key={node.key}>
          <button
            type="button"
            onClick={() => toggleGroup(node.key)}
            aria-expanded={isOpen}
            style={pad}
            className={`sb-item${nested}`}
          >
            <span className="sb-dot is-group" />
            <span className="sb-text">{node.label}</span>
            <span className="sb-chev">{node.children.length ? (isOpen ? "−" : "+") : ""}</span>
          </button>
          {isOpen && renderNodes(node.children, depth + 1)}
        </Fragment>
      );
    });

  return (
    <>
      {/* Mobile backdrop */}
      <div
        onClick={onMobileClose}
        aria-hidden="true"
        className={`fixed inset-0 z-40 bg-black/60 md:hidden transition-opacity duration-300 ${
          mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />

      <aside
        className="sb"
        style={{ transform: mobileOpen ? "translateX(0)" : "translateX(-100%)" }}
      >
        <div className="sb-stripe" aria-hidden="true" />

        <div className="sb-head">
          <div className="sb-logo-crop">
            <img src="/logo.png" alt="Lisokom" className="sb-logo" />
          </div>
          <span className="sb-badge">INV-01</span>
          <button type="button" onClick={onMobileClose} aria-label="Tutup menu" className="sb-close">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav className="sb-nav no-scrollbar" aria-label="Navigasi utama">
          {renderNodes(navTree, 0)}
        </nav>

        {/* With pembatasan per-kelompok on, lists are shorter than the same
            account used to see. Naming the kelompok next to the user is the
            difference between "the data is gone" and "I'm looking at my
            kelompok's slice of it". */}
        {user?.team_scoping_enabled && user?.role !== "superadmin" && (
          <div className="sb-teams">
            <TeamBadgeList teams={user?.teams || []} max={2} />
          </div>
        )}

        <div className="sb-foot">
          <div className="sb-avatar">
            {user?.avatar_url ? (
              <AvatarViewer src={user.avatar_url} alt={user.username} className="w-full h-full" />
            ) : (
              initials
            )}
          </div>
          <button
            type="button"
            className="sb-user"
            title="Buka profil"
            onClick={() => {
              navigate("/profile");
              if (onMobileClose) onMobileClose();
            }}
          >
            <span className="sb-user-name">{user?.username || "…"}</span>
            <span className="sb-user-role">{ROLE_LABEL[user?.role] || user?.role || ""}</span>
          </button>
          <button type="button" className="sb-logout" onClick={handleLogout}>
            KELUAR
          </button>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
